import NiceModal from "@ebay/nice-modal-react";
import { useEffect, useState } from "react";
import {
  Outlet,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router";
import { useQuery } from "@tanstack/react-query";
import "./modals";
import { getModuleValue, globalCampaignIdent, themeAvailable } from "./Helpers";
import {
  infoCampaign,
  CAMPAIGN_CACHE_TIME,
  CAMPAIGN_STALE_TIME,
} from "@api/campaign";
import {
  describeSession,
  SESSION_CACHE_TIME,
  SESSION_STALE_TIME,
} from "@api/session";
import {
  initSession,
  sendSessionEv
} from "@api/events";
import Hero from "@components/Hero";
import Loading from "@components/Statuses/Loading";
import { completeSession } from "./api/session";
import Footer from "./components/Footer";
import { getAppData, removeAppData, setAppData } from "./storageFallback";

function Layout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [theme, setTheme] = useState("generic");
  const [userSession, setUserSession] = useState({});
  const [handlerCode, setHandlerCode] = useState(
    searchParams.get("handlerCode") || searchParams.get("handlercode") || searchParams.get("event") ||
    getAppData("handlerCode") ||
    false
  );
  const [promoterCode, setPromoterCode] = useState(
    searchParams.get("promoterCode") || searchParams.get("promotercode") || searchParams.get("referral") ||
    getAppData("promoterCode") ||
    false
  );
  const [offers, setOffers] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [polls, setPolls] = useState([]);
  const [sessionUUID, setSessionUUID] = useState(
    getAppData("sessionUUID") || null
  );
  const [currentCatalogue, setCurrentCatalogue] = useState(null);
  const [allForms, setAllForms] = useState([]);
  const [completedForms, setCompletedForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [globalLoading, setGlobalLoading] = useState(false);
  const steps = 5;
  const [step, setStep] = useState(0);
  const ident = globalCampaignIdent // from .env
  const [shortHero, setShortHero] = useState(false);
  const [couponsData, setCouponsData] = useState({});

  useEffect(() => {
    setAppData("campaignIdent", ident);
  }, [ident]);

  useEffect(() => {
    if (sessionUUID) {
      setAppData("sessionUUID", sessionUUID);
      sessionRefetch();
    }
  }, [sessionUUID]);

  const clearToken = (tokenName, expireTime) => {
    const now = new Date()
    const timeInSeconds = (expireTime.getTime() - now.getTime()) / 1000

    setTimeout(() => {
      if (getAppData(tokenName)) {
        removeAppData(tokenName)
        setTimeout(() => {
          location.reload()
        }, 300)
      }
    }, timeInSeconds * 1000)
  }

  const initEventSession = (event, value) => {
    initSession((result) => {
      if (result.status.success) {
        setAppData("sId", result.data.sessionId)
        sendSessionEv(event, value)
      }
    })
  }

  const sendSessionEvent = (event, value) => {
    if (getAppData("sId")) {
      sendSessionEv(event, value)
    } else {
      initEventSession(event, value)
    }
  }

  useEffect(() => {
    if (pathname === `/code`) {
      setStep(0);
      setShortHero(false);
    } else if (pathname.includes(`/login`)) {
      setStep(1);
      setShortHero(false);
    } else if (
      pathname === `/verify/personal-details` ||
      pathname === `/verify/schufa` ||
      pathname === `/verify/survey` ||
      pathname === `/verify/phone`
    ) {
      setStep(2);
      setShortHero(true);
    } else if (pathname === `/offers`) {
      setStep(3);
      setShortHero(true);
    } else if (pathname.includes(`/offer/`)) {
      setStep(4);
      setShortHero(true);
    } else if (pathname.includes(`/coupon`)) {
      setStep(5);
      setShortHero(true);
    } else {
      setStep(0);
      setShortHero(true);
    }

    if (getAppData("verificationDetails")) {
      const token = JSON.parse(getAppData("verificationDetails"))?.token
      if (token) {
        clearToken("verificationDetails", new Date(token.expires))
      }
    }

    if (getAppData("phoneUpdateDetails")) {
      const token = JSON.parse(getAppData("phoneUpdateDetails"))?.token
      if (token) {
        clearToken("phoneUpdateDetails", new Date(token.expires))
      }
    }

    if (getAppData("surveyDetails")) {
      const token = JSON.parse(getAppData("surveyDetails"))?.token
      if (token) {
        clearToken("surveyDetails", new Date(token.expires))
      }
    }

    if (getAppData("sessionToken")) {
      const token = JSON.parse(getAppData("sessionToken"))
      if (token) {
        clearToken("sessionToken", new Date(token.expires))
      }
    }

    sendSessionEvent("pageView", pathname.replaceAll("/", "_"))
  }, [pathname]);

  useEffect(() => {
    if (getAppData("sr")) {
      sendSessionEvent("page_refresh", pathname.replaceAll("/", "_"))
    } else {
      setAppData("sr", true)
    }
  }, [])

  useEffect(() => {
    if (handlerCode) setAppData("handlerCode", handlerCode);
    if (promoterCode) setAppData("promoterCode", promoterCode);
  }, [handlerCode, promoterCode]);

  const { data: campaign } = useQuery({
    queryKey: ["campaign-info"],
    queryFn: () => infoCampaign(ident),
    retry: false,
    staleTime: CAMPAIGN_STALE_TIME,
    cacheTime: CAMPAIGN_CACHE_TIME,
  });

  const { data: session, refetch: sessionRefetch } = useQuery({
    queryKey: ["session-describe"],
    queryFn: () =>
      describeSession(
        () => { },
        (result) => {
          if (result.data.error === "error_session_completed") {
            setGlobalLoading(true);
            completeSession(
              (result) => {
                setCoupons(result.data?.rewards);
                setPolls(result.data?.polls);
                setCouponsData(result.data);
                navigate(`/coupon`);
                setGlobalLoading(false);
              },
              (result) => {
                console.log(result);
              }
            );
          }
        }
      ),
    retry: false,
    enabled:
      getAppData("sessionToken") &&
        getAppData("sessionUUID")
        ? true
        : false,
    staleTime: SESSION_STALE_TIME,
    cacheTime: SESSION_CACHE_TIME,
  });

  useEffect(() => {
    if (session) {
      setUserSession(session);
      const allForms = session?.catalogues.map((el) => el.forms).flat(1);
      const completedForms = allForms.filter((el) => el.isComplete);
      setAllForms(allForms || []);
      setCompletedForms(completedForms || []);
      if (allForms.length === 0) {

      } else if (allForms.length === completedForms.length && coupons.length === 0) {
        setGlobalLoading(true);
        completeSession(
          (result) => {
            setCoupons(result.data?.rewards);
            setPolls(result.data?.polls);
            setCouponsData(result.data);
            navigate(`/coupon`);
            setGlobalLoading(false);
          },
          (result) => {
            console.log(result);
          }
        );
      }
    }
  }, [session]);

  useEffect(() => {
    if (campaign) {
      if (campaign?.branding && themeAvailable(campaign?.branding)) {
        document.body.classList.add(campaign?.branding);
        import(`../styles/themes/dynamic.${campaign?.branding}.scss`).then(
          () => {
            setTimeout(() => {
              setLoading(false);
            }, 1000);
          }
        );
      } else {
        setLoading(false);
      }

      if (campaign.promoter && !searchParams.get("promoterCode") && !searchParams.get("promotercode") && !searchParams.get("referral")) {
        setPromoterCode(campaign.promoter);
      }

      if (campaign.store && !searchParams.get("handlerCode") && !searchParams.get("handlercode") && !searchParams.get("event")) {
        setHandlerCode(campaign.store);
      }

      if (
        (!handlerCode && !campaign.store) ||
        (!promoterCode && !campaign.promoter)
      ) {
        navigate(`/code`, { replace: true });
      } else if (getAppData("verificationDetails")) {
        const details = JSON.parse(
          getAppData("verificationDetails")
        );

        if (details?.verification?.yotiPermitted) {
          navigate(`/verify/personal-details`);
        } else if (details?.verification?.schufaPermitted) {
          navigate(`/verify/personal-details`);
        }
      } else if (getAppData("phoneUpdateDetails")) {
        navigate(`/verify/phone`);
      } else if (getAppData("surveyDetails")) {
        // Open BE Q: survey before vs after age verify if both could apply
        navigate(`/verify/survey`);
      } else if (!getAppData("sessionToken") && pathname !== `/login/otp`) {
        navigate(`/login`);
      } else if (
        getAppData("sessionUUID") &&
        !pathname.includes("offer") &&
        !pathname.includes("coupon")
      ) {
        navigate(`/offer`);
      }
    }
  }, [campaign]);

  return (
    <NiceModal.Provider>
      {loading ? (
        <Loading fullScreen>
          <p>Gib uns einen Augenblick</p>
        </Loading>
      ) : (
        <main className="app-wrapper">
          <button
            tabIndex={0}
            className="logout-top"
            onClick={() => {
              removeAppData("handlerCode");
              removeAppData("promoterCode");
              removeAppData("sessionToken");
              removeAppData("phoneUpdateDetails");
              removeAppData("verificationDetails");
              removeAppData("surveyDetails");
              removeAppData("sessionUUID");
              document.location.href = `/`;
            }}
          >
            Ausloggen
          </button>
          <Hero
            short={shortHero}
            hideBranding={false}
            brands={campaign?.brands || []}
            bg={campaign?.content?.modules}
            loggedIn={session ? true : false}
          />
          <Outlet
            context={{
              theme,
              userSession,
              promoterCode,
              setPromoterCode,
              handlerCode,
              setHandlerCode,
              campaign,
              steps,
              step,
              ident,
              offers,
              setOffers,
              sessionUUID,
              setSessionUUID,
              currentCatalogue,
              setCurrentCatalogue,
              allForms,
              completedForms,
              coupons,
              setCoupons,
              polls,
              setPolls,
              couponsData,
              setCouponsData,
              globalLoading
            }}
          />
          <Footer branding={campaign?.branding || "generic"} />
        </main>
      )}
    </NiceModal.Provider>
  );
}

export default Layout;
